import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client.js';
import { formatBrl } from '../../utils/currency.js';
import { cepDigits, formatCep } from '../../utils/cep.js';

function formatCepDisplay(raw) {
  const d = cepDigits(raw);
  return d.length === 8 ? formatCep(d) : '—';
}

function findGaps(zones) {
  const active = [...(zones || [])]
    .filter((z) => Number(z.active) === 1)
    .sort((a, b) => String(a.cep_start).localeCompare(String(b.cep_start)));
  if (active.length < 2) return [];
  const gaps = [];
  for (let i = 1; i < active.length; i++) {
    const prevEnd = String(Number(active[i - 1].cep_end) + 1).padStart(8, '0');
    const currStart = String(active[i].cep_start);
    if (prevEnd < currStart) {
      gaps.push({
        from: prevEnd,
        to: String(Number(currStart) - 1).padStart(8, '0'),
      });
    }
  }
  return gaps;
}

const EMPTY_ZONE = {
  name: '',
  cep_start: '',
  cep_end: '',
  fee: '',
  estimated_days: '1',
  active: true,
};

/**
 * Gestão de faixas de CEP por tenant (painel Empresas).
 * @param {{ tenantId: number, pickupOnly?: boolean }} props
 */
export function AdminDeliveryZonesSection({ tenantId, pickupOnly = false }) {
  const [zones, setZones] = useState([]);
  const [cepOrigin, setCepOrigin] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMsg, setDialogMsg] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ZONE);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setMsg('');
    try {
      const d = await api(`/tenants/${tenantId}/delivery-zones`);
      if (d?.schema_note) {
        setZones([]);
        setMsg(d.schema_note);
        return;
      }
      setZones(Array.isArray(d?.zones) ? d.zones : []);
      const co = d?.cep_origin ? formatCep(String(d.cep_origin)) : '';
      setCepOrigin(co);
    } catch (e) {
      setZones([]);
      const detail =
        e?.data?.detail != null && String(e.data.detail).trim() !== ''
          ? ` — ${String(e.data.detail)}`
          : '';
      if (e?.status === 404) {
        setMsg(
          'Rota da API não encontrada. Confirme que subiu api/inc/routes.php e api/inc/delivery_zones.php e reinicie o PHP (php -S … router.php).',
        );
      } else {
        setMsg((e.message || 'Erro ao carregar zonas.') + detail);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const gaps = useMemo(() => findGaps(zones), [zones]);

  async function saveCepOrigin() {
    const digits = cepDigits(cepOrigin);
    if (digits.length !== 8 && cepOrigin.trim() !== '') {
      setMsg('CEP de origem inválido.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await api(`/tenants/${tenantId}/delivery-zones/cep-origin`, {
        method: 'PATCH',
        body: JSON.stringify({ cep_origin: digits.length === 8 ? digits : null }),
      });
      setMsg('CEP de origem guardado.');
    } catch (e) {
      const detail =
        e?.data?.detail != null && String(e.data.detail).trim() !== ''
          ? ` — ${String(e.data.detail)}`
          : '';
      if (e?.status === 503 && e?.data?.code === 'SCHEMA_OUTDATED') {
        setMsg(
          'Falta a coluna cep_origin na base. Execute database/0001_schema.sql.',
        );
      } else {
        setMsg((e.message || 'Erro ao guardar CEP de origem.') + detail);
      }
    } finally {
      setBusy(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_ZONE);
    setDialogMsg('');
    setDialogOpen(true);
  }

  function openEdit(zone) {
    setEditing(zone);
    setDialogMsg('');
    setForm({
      name: zone.name || '',
      cep_start: formatCep(zone.cep_start || ''),
      cep_end: formatCep(zone.cep_end || ''),
      fee: String(zone.fee ?? ''),
      estimated_days: String(zone.estimated_days ?? 1),
      active: Number(zone.active) === 1,
    });
    setDialogOpen(true);
  }

  async function saveZone(ev) {
    ev.preventDefault();
    if (!tenantId || tenantId < 1) {
      setDialogMsg('Empresa inválida. Feche e abra novamente «Configurações».');
      return;
    }
    const dStart = cepDigits(form.cep_start);
    const dEnd = cepDigits(form.cep_end);
    const feeRaw = String(form.fee).trim();
    let feeParsed;
    if (/^\d{1,3}(\.\d{3})+,\d{2}$/.test(feeRaw)) {
      feeParsed = Number(feeRaw.replace(/\./g, '').replace(',', '.'));
    } else {
      feeParsed = Number(feeRaw.replace(',', '.'));
    }
    const daysParsed = Number(String(form.estimated_days).trim());
    if (form.name.trim().length < 2) {
      setDialogMsg('Indique um nome da zona com pelo menos 2 caracteres.');
      return;
    }
    if (dStart.length !== 8 || dEnd.length !== 8) {
      setDialogMsg('Informe os dois CEPs completos com 8 dígitos (ex.: 74000-000).');
      return;
    }
    if (!Number.isFinite(feeParsed) || feeParsed < 0) {
      setDialogMsg('Taxa inválida. Use um valor numérico (ex.: 8 ou 8,50).');
      return;
    }
    if (!Number.isFinite(daysParsed) || daysParsed < 1 || daysParsed > 30) {
      setDialogMsg('Prazo deve ser entre 1 e 30 dias.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      cep_start: dStart,
      cep_end: dEnd,
      fee: feeParsed,
      estimated_days: daysParsed,
      active: Boolean(form.active),
    };
    setBusy(true);
    setMsg('');
    setDialogMsg('');
    try {
      if (editing?.id) {
        await api(`/tenants/${tenantId}/delivery-zones/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await api(`/tenants/${tenantId}/delivery-zones`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setDialogOpen(false);
      await load();
      setMsg(editing?.id ? 'Zona actualizada.' : 'Zona criada.');
    } catch (e) {
      const code =
        e?.data != null && typeof e.data === 'object' && e.data.code != null
          ? String(e.data.code)
          : '';
      const hint =
        e?.status === 503 || code === 'SCHEMA_OUTDATED'
          ? ' Execute database/0001_schema.sql nesta base (tabela tenant_delivery_zones).'
          : '';
      const full = (e.message || 'Erro ao guardar zona.') + hint;
      setDialogMsg(full);
      setMsg(full);
    } finally {
      setBusy(false);
    }
  }

  async function removeZone(id) {
    if (!window.confirm('Remover esta zona de entrega?')) return;
    setBusy(true);
    setMsg('');
    try {
      await api(`/tenants/${tenantId}/delivery-zones/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setMsg(e.message || 'Erro ao remover.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-xs text-ink/60 dark:text-frost-muted">A carregar zonas de entrega…</p>;
  }

  return (
    <div className="space-y-3 rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-4 dark:border-sky-400/25 dark:bg-sky-500/10">
      <div>
        <p className="text-xs font-semibold text-ink dark:text-mist">Zonas de entrega por CEP</p>
        <p className="mt-1 text-[11px] leading-relaxed text-ink/60 dark:text-frost-muted">
          Com pelo menos uma faixa activa, o checkout calcula a taxa pelo CEP (campo explícito ou texto da morada).
          Sem faixas activas, o comportamento do checkout mantém-se como antes (sem taxa de entrega).
        </p>
        {pickupOnly ? (
          <p className="mt-2 text-[11px] font-medium text-amber-800 dark:text-amber-200">
            Esta loja está em «só retirada» — as zonas só aplicam se activar entregas.
          </p>
        ) : null}
      </div>

      {msg ? <p className="text-[11px] font-medium text-ink/80 dark:text-mist/90">{msg}</p> : null}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[11px] font-semibold text-ink dark:text-mist">
          CEP de origem (referência)
          <input
            className="mt-1 block w-40 rounded-lg border border-ink/10 bg-white px-2 py-1.5 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
            placeholder="00000-000"
            value={cepOrigin}
            onChange={(e) => setCepOrigin(formatCep(e.target.value))}
            disabled={busy}
          />
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={saveCepOrigin}
          className="rounded-lg border border-ink/15 px-3 py-1.5 text-[11px] font-bold uppercase dark:border-white/15"
        >
          Guardar CEP
        </button>
      </div>

      {gaps.length > 0 ? (
        <p className="rounded-lg border border-amber-400/40 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
          Lacunas entre faixas activas:{' '}
          {gaps.map((g, i) => (
            <span key={i} className="font-mono">
              {formatCepDisplay(g.from)} → {formatCepDisplay(g.to)}
              {i < gaps.length - 1 ? '; ' : ''}
            </span>
          ))}
        </p>
      ) : null}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={busy}
          onClick={openCreate}
          className="rounded-lg bg-[var(--st-600,#0d9488)] px-3 py-1.5 text-[11px] font-bold uppercase text-white"
        >
          Nova faixa
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-ink/10 dark:border-white/10">
        <table className="w-full min-w-[520px] border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-ink/10 bg-zinc-100/80 text-[10px] font-bold uppercase dark:border-white/10 dark:bg-zinc-900/80">
              <th className="px-2 py-2">Zona</th>
              <th className="px-2 py-2 text-center">CEP início</th>
              <th className="px-2 py-2 text-center">CEP fim</th>
              <th className="px-2 py-2 text-right">Taxa</th>
              <th className="px-2 py-2 text-center">Prazo</th>
              <th className="px-2 py-2 text-center">Estado</th>
              <th className="px-2 py-2 text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {zones.map((z) => (
              <tr key={z.id} className="border-b border-ink/5 dark:border-white/5">
                <td className="px-2 py-2 font-medium">{z.name}</td>
                <td className="px-2 py-2 text-center font-mono">{formatCepDisplay(z.cep_start)}</td>
                <td className="px-2 py-2 text-center font-mono">{formatCepDisplay(z.cep_end)}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold">{formatBrl(z.fee)}</td>
                <td className="px-2 py-2 text-center tabular-nums">
                  {z.estimated_days} {Number(z.estimated_days) === 1 ? 'dia' : 'dias'}
                </td>
                <td className="px-2 py-2 text-center">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${Number(z.active) === 1 ? 'bg-emerald-500/20 text-emerald-900 dark:text-emerald-100' : 'bg-zinc-500/20'}`}
                  >
                    {Number(z.active) === 1 ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
                <td className="px-2 py-2 text-center">
                  <button type="button" className="mr-2 text-[10px] font-bold underline" onClick={() => openEdit(z)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className="text-[10px] font-bold uppercase text-red-600 underline"
                    onClick={() => removeZone(z.id)}
                  >
                    Remover
                  </button>
                </td>
              </tr>
            ))}
            {zones.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-ink/55 dark:text-frost-muted">
                  Nenhuma faixa cadastrada.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {dialogOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4"
              role="presentation"
              style={{ isolation: 'isolate' }}
              onMouseDown={(e) => {
                if (busy) return;
                if (e.target === e.currentTarget) setDialogOpen(false);
              }}
            >
              <form
                className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-zinc-900"
                onSubmit={saveZone}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-display text-lg font-semibold">
                  {editing ? 'Editar zona' : 'Nova zona de entrega'}
                </h3>
                {dialogMsg ? (
                  <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-950 dark:border-amber-400/35 dark:bg-amber-950/40 dark:text-amber-100">
                    {dialogMsg}
                  </p>
                ) : null}
                <div className="mt-4 space-y-3 text-sm">
                  <label className="block font-semibold">
                    Nome
                    <input
                      required
                      className="mt-1 w-full rounded-lg border px-3 py-2 dark:border-zinc-600 dark:bg-zinc-950"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="font-semibold">
                      CEP inicial
                      <input
                        required
                        className="mt-1 w-full rounded-lg border px-3 py-2 font-mono dark:border-zinc-600 dark:bg-zinc-950"
                        value={form.cep_start}
                        onChange={(e) => setForm((f) => ({ ...f, cep_start: formatCep(e.target.value) }))}
                      />
                    </label>
                    <label className="font-semibold">
                      CEP final
                      <input
                        required
                        className="mt-1 w-full rounded-lg border px-3 py-2 font-mono dark:border-zinc-600 dark:bg-zinc-950"
                        value={form.cep_end}
                        onChange={(e) => setForm((f) => ({ ...f, cep_end: formatCep(e.target.value) }))}
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="font-semibold">
                      Taxa (R$)
                      <input
                        required
                        type="number"
                        min={0}
                        step="0.5"
                        className="mt-1 w-full rounded-lg border px-3 py-2 dark:border-zinc-600 dark:bg-zinc-950"
                        value={form.fee}
                        onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))}
                      />
                    </label>
                    <label className="font-semibold">
                      Prazo (dias)
                      <input
                        required
                        type="number"
                        min={1}
                        max={30}
                        className="mt-1 w-full rounded-lg border px-3 py-2 dark:border-zinc-600 dark:bg-zinc-950"
                        value={form.estimated_days}
                        onChange={(e) => setForm((f) => ({ ...f, estimated_days: e.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    />
                    Zona activa
                  </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-4 py-2 text-sm"
                    onClick={() => {
                      setDialogMsg('');
                      setDialogOpen(false);
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-bold text-white dark:bg-[var(--st-600)]"
                  >
                    {busy ? 'A guardar…' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
